import React, { useState } from "react";
import Modal from "react-modal";
import ButtonCustom from "../../utils/custom-button";
import { UserModel } from "../../models/UserModel";
import { validateUser, rejectUser } from "../../services/User_service";
import "../style/UserValidationModal.css";

interface UserValidationModalProps {
  isOpen: boolean;
  user: UserModel | null;
  onClose: () => void;
}

interface Field {
  key: string;
  label: string;
  icon: string;
  formatter?: (val: any) => string;
}

const UserValidationModal: React.FC<UserValidationModalProps> = ({
  isOpen,
  user,
  onClose,
}) => {
  const [showRejectReason, setShowRejectReason] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [loading, setLoading] = useState(false);

  if (!user) return null;

  const isProfessional = user.user_type === "professionnel";
  const isEntreprise = user.user_type === "entreprise";

  // Fonction pour formater le RIB avec espaces
  const formatRIB = (rib: string | null | undefined) => {
    if (!rib) return "-";
    const clean = rib.replace(/\s/g, '');
    if (clean.length !== 23) return rib;
    const bank = clean.slice(0, 5);
    const guichet = clean.slice(5, 10);
    const compte = clean.slice(10, 21);
    const cle = clean.slice(21);
    return `${bank} ${guichet} ${compte} ${cle}`;
  };

  // Fonction pour formater le statut d'annuaire
  const formatDirectoryStatus = (value: boolean | null | undefined) => {
    if (value === true) return "✅ Oui - Visible dans l'annuaire";
    if (value === false) return "❌ Non - Masqué de l'annuaire";
    return "-";
  };

  // Champs communs à tous les types d'utilisateurs
  const commonFields: Field[] = [
    { key: "name", label: "Nom", icon: "bi-person-vcard" },
    { key: "firstname", label: "Prénom", icon: "bi-person" },
    {
      key: "civility", label: "Civilité", icon: "bi-gender-ambiguous",
      formatter: (val: string) => val === 'monsieur' ? 'Monsieur' : 'Madame'
    },
    { key: "profession", label: "Profession", icon: "bi-briefcase" },
    { key: "email", label: "Email", icon: "bi-envelope-at" },
    { key: "number", label: "Téléphone", icon: "bi-telephone" },
    { key: "postal_code", label: "Code postal", icon: "bi-mailbox" },
    { key: "country", label: "Ville", icon: "bi-geo-alt" },
    { key: "adresse", label: "Adresse", icon: "bi-house-door" },
    {
      key: "createdAt", label: "Date d'inscription", icon: "bi-calendar",
      formatter: (val: string) => val ? new Date(val).toLocaleDateString('fr-FR') : "-"
    },
  ];

  // Champs spécifiques aux entreprises et professionnels
  const businessFields: Field[] = [
    { key: "raison_social", label: "Raison sociale", icon: "bi-building" },
    { key: "code_SIREN", label: "SIREN", icon: "bi-credit-card" },
    { key: "code_NAF", label: "Code NAF", icon: "bi-file-earmark-text" },
    { key: "tv_intra", label: "TVA Intracommunautaire", icon: "bi-receipt" },
    // visibilité annuaire ajoutée pour entreprises également
    {
      key: "show_in_directory",
      label: "Visibilité dans l'annuaire",
      icon: "bi-eye",
      formatter: (val: boolean | null | undefined) => formatDirectoryStatus(val),
    },
  ];

  const professionalSpecificFields: Field[] = [
    {
      key: "identity_document_type", label: "Type de pièce d'identité", icon: "bi-card-text",
      formatter: (val: string) => val === 'cin' ? 'Carte d\'Identité Nationale' : 'Passeport'
    },
    {
      key: "documents_verified", label: "Documents vérifiés", icon: "bi-check-circle",
      formatter: (val: boolean) => val ? "✅ Oui" : "❌ Non"
    },
    {
      key: "is_validated", label: "Compte validé", icon: "bi-shield-check",
      formatter: (val: boolean) => val ? "✅ Oui" : "⏳ En attente"
    },
    {
      key: "status", label: "Statut", icon: "bi-info-circle",
      formatter: (val: string) => {
        switch (val) {
          case 'active': return '🟢 Actif';
          case 'inactive': return '🔴 Inactif';
          case 'waiting_validation': return '🟡 En attente de validation';
          default: return val || "-";
        }
      }
    },
  ];

  // Champs coordonnées bancaires pour professionnels
  const bankingFields: Field[] = [
    {
      key: "account_number",
      label: "RIB (23 caractères)",
      icon: "bi-credit-card-2-front",
      formatter: (val: string) => formatRIB(val)
    },
    {
      key: "bic_swift",
      label: "BIC/SWIFT",
      icon: "bi-bank"
    },
  ];

  
  const openDocument = (docPath: string | null | undefined, docType: string) => {
    if (!docPath) {
      alert(`Document ${docType} non disponible`);
      return;
    }
    const apiUrl = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
    window.open(`${apiUrl}/${docPath}`, '_blank');
  };

  
  const handleValidateUser = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      await validateUser(user.id);
      alert("Utilisateur validé avec succès ✅");
      onClose();
      window.location.reload();
    } catch (error) {
      console.error("Erreur validation utilisateur :", error);
      alert("Erreur lors de la validation ❌");
    } finally {
      setLoading(false);
    }
  };

  const handleRejectUser = async () => {
    if (!user?.id) return;
    if (!rejectReason.trim()) {
      alert("Veuillez entrer une raison de rejet.");
      return;
    }
    setLoading(true);
    try {
      await rejectUser(user.id, rejectReason);
      alert("Utilisateur rejeté avec succès");
      onClose();
      window.location.reload();
    } catch (error) {
      console.error("Erreur de rejection utilisateur:", error);
      alert("Erreur lors du rejet ❌");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = () => {
    if (isProfessional) return "#4bb0c0";
    if (isEntreprise) return "#F4A261";
    return "#9e9e9e";
  };

  const getStatusText = () => {
    if (isProfessional) return "PROFESSIONNEL";
    if (isEntreprise) return "ENTREPRISE";
    return "PARTICULIER";
  };

  // ✅ Fonction pour obtenir la classe CSS du badge d'annuaire
  const getDirectoryBadgeClass = (value: boolean | null | undefined) => {
    if (value === true) return "directory-badge visible";
    if (value === false) return "directory-badge hidden";
    return "directory-badge unknown";
  };

  // ✅ Fonction pour afficher un champ avec badge pour show_in_directory
  const renderFieldValue = (field: Field, value: any) => {
    if (field.key === 'show_in_directory') {
      return (
        <span className={getDirectoryBadgeClass(value)}>
          <i className={`bi ${value ? 'bi-eye' : 'bi-eye-slash'} me-1`}></i>
          {field.formatter ? field.formatter(value) : (value !== undefined && value !== null ? String(value) : "-")}
        </span>
      );
    }
    return field.formatter ? field.formatter(value) : (value || "-");
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Validation utilisateur"
      className="user-validation-modal"
      overlayClassName="user-validation-overlay"
    >
      <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
        <div className="modal-header-custom">
          <div className="header-content">
            <div className="user-avatar">
              <i className="bi bi-person-circle"></i>
            </div>
            <div className="header-info">
              <h2 className="modal-title">
                {user.firstname} {user.name}
              </h2>
              <div className="user-meta">
                <span className="user-id">ID: {user.id}</span>
                <span className="user-email"style={{ color: 'white' }}>
                  <i className="bi bi-envelope me-1"></i>
                  {user.email}
                </span>
              </div>
            </div>
          </div>
          <div className="status-badge" style={{ backgroundColor: getStatusColor() }}>
            {getStatusText()}
          </div>
        </div>

        {/* Section informations générales */}
        <div className="info-section">
          <div className="section-header">
            <i className="bi bi-person-lines-fill"></i>
            <h3>Informations générales</h3>
          </div>
          <div className="info-grid">
            {commonFields.map((field, i) => (
              <div className="info-item" key={field.key + i}>
                <div className="info-label">
                  <i className={`${field.icon} me-2`}></i>
                  {field.label}
                </div>
                <div className="info-value">
                  {renderFieldValue(field, (user as any)[field.key])}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section entreprise/professionnel */}
        {(isEntreprise || isProfessional) && (
          <div className="info-section">
            <div className="section-header">
              <i className="bi bi-building"></i>
              <h3>{isProfessional ? "Informations professionnelles" : "Informations entreprise"}</h3>
            </div>
            <div className="info-grid">
              {businessFields.map((field, i) => (
                <div className="info-item" key={field.key + i}>
                  <div className="info-label">
                    <i className={`${field.icon} me-2`}></i>
                    {field.label}
                  </div>
                  <div className="info-value">
                    {renderFieldValue(field, (user as any)[field.key])}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section coordonnées bancaires (professionnels uniquement) */}
        {isProfessional && (
          <div className="info-section banking-section">
            <div className="section-header">
              <i className="bi bi-bank2"></i>
              <h3>Coordonnées bancaires</h3>
            </div>
            <div className="banking-info-box">
              <div className="banking-warning">
                <i className="bi bi-info-circle me-2"></i>
                <span>Ces informations sont utilisées pour les remboursements et paiements</span>
              </div>
              <div className="info-grid">
                {bankingFields.map((field, i) => (
                  <div className="info-item banking-item" key={field.key + i}>
                    <div className="info-label">
                      <i className={`${field.icon} me-2`}></i>
                      {field.label}
                    </div>
                    <div className="info-value banking-value">
                      {renderFieldValue(field, (user as any)[field.key])}
                    </div>
                  </div>
                ))}
              </div>

              {/* Vérification des coordonnées bancaires */}
              <div className="banking-status mt-3">
                {user.account_number && user.bic_swift ? (
                  <div className="status-complete">
                    <i className="bi bi-check-circle-fill"></i>
                    <span>Coordonnées bancaires complètes</span>
                  </div>
                ) : (
                  <div className="status-incomplete">
                    <i className="bi bi-exclamation-triangle-fill"></i>
                    <span>Coordonnées bancaires incomplètes</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Section spécifique aux professionnels */}
        {isProfessional && (
          <>
            <div className="info-section">
              <div className="section-header">
                <i className="bi bi-shield-check"></i>
                <h3>Statut et vérification</h3>
              </div>
              <div className="info-grid">
                {professionalSpecificFields.map((field, i) => (
                  <div className="info-item" key={field.key + i}>
                    <div className="info-label">
                      <i className={`${field.icon} me-2`}></i>
                      {field.label}
                    </div>
                    <div className="info-value">
                      {renderFieldValue(field, (user as any)[field.key])}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Section documents */}
            <div className="documents-section">
              <div className="section-header">
                <i className="bi bi-folder2-open"></i>
                <h3>Documents fournis</h3>
              </div>

              <div className="documents-grid">
                {/* Kbis */}
                <div className="document-card">
                  <div className="document-header">
                    <i className="bi bi-file-earmark-pdf document-icon kbis"></i>
                    <div className="document-info">
                      <h4>Extrait Kbis</h4>
                      <p className="document-description">
                        Document légal justifiant l'existence de l'entreprise
                      </p>
                    </div>
                  </div>
                  <div className="document-actions">
                    {user.kbis_document ? (
                      <button
                        className="btn-view-document kbis"
                        onClick={() => openDocument(user.kbis_document, "Kbis")}
                      >
                        <i className="bi bi-eye me-2"></i>
                        Voir le document
                      </button>
                    ) : (
                      <div className="no-document">
                        <i className="bi bi-file-x"></i>
                        <span>Document non fourni</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Pièce d'identité Recto */}
                <div className="document-card">
                  <div className="document-header">
                    <i className="bi bi-person-badge document-icon identity"></i>
                    <div className="document-info">
                      <h4>Pièce d'identité - Recto</h4>
                      <p className="document-description">
                        {user.identity_document_type === 'cin'
                          ? "Carte d'Identité Nationale"
                          : "Passeport"}
                      </p>
                    </div>
                  </div>
                  <div className="document-actions">
                    {user.identity_document_recto ? (
                      <button
                        className="btn-view-document identity"
                        onClick={() => openDocument(user.identity_document_recto, "Recto")}
                      >
                        <i className="bi bi-eye me-2"></i>
                        Voir le document
                      </button>
                    ) : (
                      <div className="no-document">
                        <i className="bi bi-file-x"></i>
                        <span>Document non fourni</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Pièce d'identité Verso (si CIN) */}
                {user.identity_document_type === 'cin' && (
                  <div className="document-card">
                    <div className="document-header">
                      <i className="bi bi-person-badge document-icon identity"></i>
                      <div className="document-info">
                        <h4>Pièce d'identité - Verso</h4>
                        <p className="document-description">
                          Verso de la Carte d'Identité Nationale
                        </p>
                      </div>
                    </div>
                    <div className="document-actions">
                      {user.identity_document_verso ? (
                        <button
                          className="btn-view-document identity"
                          onClick={() => openDocument(user.identity_document_verso, "Verso")}
                        >
                          <i className="bi bi-eye me-2"></i>
                          Voir le document
                        </button>
                      ) : (
                        <div className="no-document">
                          <i className="bi bi-file-x"></i>
                          <span>Document non fourni</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Document RIB */}
                <div className="document-card">
                  <div className="document-header">
                    <i className="bi bi-bank document-icon rib" aria-hidden="true"></i>
                    <div className="document-info">
                      <h4>Relevé d'Identité Bancaire (RIB)</h4>
                      <p className="document-description">
                        Justificatif bancaire pour remboursements
                      </p>
                    </div>
                  </div>

                  <div className="document-actions">
                    {user.rib_document ? (
                      <button
                        type="button"
                        className="btn-view-document rib"
                        onClick={() => openDocument(user.rib_document, "RIB")}
                        aria-label="Voir le RIB"
                      >
                        <i className="bi bi-eye me-2" aria-hidden="true"></i>
                        Voir le document
                      </button>
                    ) : (
                      <div className="no-document" aria-hidden="false">
                        <i className="bi bi-file-x"></i>
                        <span>Document non fourni</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Guide de vérification amélioré */}
              <div className="verification-guide">
                <div className="guide-header">
                  <i className="bi bi-info-circle"></i>
                  <h4>Guide de vérification</h4>
                </div>
                <ul className="guide-list">
                  <li><i className="bi bi-check-circle"></i> Kbis datant de moins de 3 mois</li>
                  <li><i className="bi bi-check-circle"></i> Pièce d'identité en cours de validité</li>
                  <li><i className="bi bi-check-circle"></i> Document RIB lisible et complet</li>
                  <li><i className="bi bi-check-circle"></i> Correspondance RIB document vs RIB saisi</li>
                  <li><i className="bi bi-check-circle"></i> Correspondance des informations (SIREN, nom, BIC)</li>
                  <li><i className="bi bi-check-circle"></i> Documents lisibles et complets</li>
                </ul>
              </div>
            </div>
          </>
        )}

        {/* Formulaire de rejet */}
        {showRejectReason && (
          <div className="reject-form-section">
            <div className="section-header warning">
              <i className="bi bi-exclamation-triangle"></i>
              <h3>Motif du rejet</h3>
            </div>
            <div className="reject-form-content">
              <p className="form-instruction">
                Veuillez indiquer la raison précise du rejet. Ce motif sera communiqué à l'utilisateur.
              </p>
              <div className="form-group">
                <label htmlFor="rejectReason">
                  <i className="bi bi-chat-left-text me-2"></i>
                  Raison du rejet *
                </label>
                <textarea
                  id="rejectReason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="form-control"
                  rows={4}
                  placeholder={isProfessional
                    ? "Ex: Kbis expiré, RIB illisible, coordonnées bancaires incorrectes, documents non conformes..."
                    : "Ex: Informations incomplètes, documents manquants, données incorrectes..."}
                  maxLength={500}
                />
                <div className="character-count">
                  {rejectReason.length}/500 caractères
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Boutons d'action */}
        <div className="action-buttons">
          {!showRejectReason ? (
            <>
              <ButtonCustom
                title={loading ? "Validation en cours..." : "VALIDER LE COMPTE"}
                color="#4BB0C0"
                onClick={handleValidateUser}
                disabled={loading}
              />
              <ButtonCustom
                title="REJETER LE COMPTE"
                color="#F4A261"
                onClick={() => setShowRejectReason(true)}
                disabled={loading}
              />
            </>
          ) : (
            <>
              <ButtonCustom
                title="ANNULER"
                color="#9e9e9e"
                onClick={() => {
                  setShowRejectReason(false);
                  setRejectReason("");
                }}
                disabled={loading}
              />
              <ButtonCustom
                title={loading ? "Rejet en cours..." : "CONFIRMER LE REJET"}
                color="#F4A261"
                onClick={handleRejectUser}
                disabled={loading || !rejectReason.trim()}
              />
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default UserValidationModal;